'use client'

import { Activity, GripVertical, RotateCcwIcon, X } from 'lucide-react'
import { useRef, useState, useSyncExternalStore } from 'react'

import { getWsDebugSnapshot, resetWsDebug, wsDebugSubscribe, type WsEventKind } from './wsDebug'
import { Button, cn, Switch } from '@fp/ui-kit'
import { WS_DEBUG_ENABLED } from '@lib/constants'
import { setDebugSettings, useDebugSettings } from '@lib/debugSettings'

const KIND_COLOR: Record<WsEventKind, string> = {
  subscribe: 'text-success',
  unsubscribe: 'text-destructive',
  emit: 'text-warning',
  received: 'text-primary',
}

/** Keeps the panel header reachable even if the stored position outgrows the viewport. */
const clampedPanelStyle = ({ x, y }: { x: number; y: number }): React.CSSProperties => ({
  top: `max(0px, min(${y}px, 100vh - 2rem))`,
  left: `max(0px, min(${x}px, 100vw - 4rem))`,
  right: 'auto',
  bottom: 'auto',
})

/**
 * DEV/QA-only WebSocket diagnostics, gated by WS_DEBUG_ENABLED (env opt-in, never
 * production). The trigger button stays hidden until hovered (like the mock-mode
 * button); clicking it opens a live stats panel that proves how often the socket
 * layer is exercised — `subscribe`/`unsubscribe` climbing in lockstep means the
 * handler is torn down + re-added on every render. Open state and dragged position
 * persist in the debug-settings store, so the panel survives page reloads.
 */
export const WsDebugOverlay = () => {
  const snap = useSyncExternalStore(wsDebugSubscribe, getWsDebugSnapshot, getWsDebugSnapshot)
  const { renderCounters, wsPanelOpen, wsPanelPos } = useDebugSettings()

  // Live position while dragging; the store value takes over once the drag is released.
  const [dragPos, setDragPos] = useState<{ x: number; y: number } | null>(null)
  const dragOffset = useRef<{ dx: number; dy: number } | null>(null)
  const pos = dragPos ?? wsPanelPos

  const startDrag = (e: React.PointerEvent<HTMLElement>) => {
    const panel = e.currentTarget.closest('[data-slot="ws-debug-panel"]')
    if (!panel) return
    const rect = panel.getBoundingClientRect()
    dragOffset.current = { dx: e.clientX - rect.left, dy: e.clientY - rect.top }
    e.currentTarget.setPointerCapture(e.pointerId)
  }

  const onDrag = (e: React.PointerEvent<HTMLElement>) => {
    if (!dragOffset.current) return
    setDragPos({ x: e.clientX - dragOffset.current.dx, y: e.clientY - dragOffset.current.dy })
  }

  const endDrag = (e: React.PointerEvent<HTMLElement>) => {
    const offset = dragOffset.current
    dragOffset.current = null
    e.currentTarget.releasePointerCapture(e.pointerId)
    if (offset) setDebugSettings({ wsPanelPos: { x: e.clientX - offset.dx, y: e.clientY - offset.dy } })
    setDragPos(null)
  }

  if (!WS_DEBUG_ENABLED) return null

  if (!wsPanelOpen) {
    return (
      <Button
        variant='ghost'
        aria-label='Open WebSocket debug panel'
        title='WebSocket debug'
        onClick={() => setDebugSettings({ wsPanelOpen: true })}
        className={cn(
          'fixed right-6 w-6 h-6 p-0 top-0 z-50 flex items-center justify-center rounded-bl-md rounded-tr-none',
          'opacity-0 backdrop-blur-sm transition-opacity',
          'hover:opacity-100 focus-visible:opacity-100 [&_svg]:size-3.5',
        )}
      >
        <Activity />
      </Button>
    )
  }

  const { totals, perSocket, log } = snap

  return (
    <div
      data-slot='ws-debug-panel'
      style={pos ? clampedPanelStyle(pos) : undefined}
      className={cn(
        'fixed bottom-2 right-2 z-[9999] w-72 rounded-lg border border-border bg-background/95 text-xs shadow-lg backdrop-blur',
        dragPos && 'select-none',
      )}
    >
      <div
        onPointerDown={startDrag}
        onPointerMove={onDrag}
        onPointerUp={endDrag}
        className='flex cursor-grab items-center justify-between gap-2 rounded-t-lg border-b border-border px-2 py-1 active:cursor-grabbing'
      >
        <span className='flex items-center gap-1.5 font-semibold'>
          <GripVertical className='size-3.5 text-muted-foreground' />
          WS activity
        </span>
        <div className='flex items-center gap-2' onPointerDown={(e) => e.stopPropagation()}>
          <button
            type='button'
            className='mr-2 flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground'
            onClick={resetWsDebug}
          >
            <RotateCcwIcon className='size-2.5' />
            Reset
          </button>
          <button
            type='button'
            className='text-muted-foreground hover:text-foreground [&_svg]:size-3.5'
            onClick={() => setDebugSettings({ wsPanelOpen: false })}
            aria-label='Hide WS panel'
          >
            <X />
          </button>
        </div>
      </div>

      <div className='flex max-h-72 flex-col p-2'>
        <div className='grid grid-cols-4 gap-1 text-center tabular-nums'>
          {(Object.keys(totals) as WsEventKind[]).map((kind) => (
            <div key={kind} className='rounded bg-muted/50 px-1 py-1'>
              <div className={`text-sm font-bold ${KIND_COLOR[kind]}`}>{totals[kind]}</div>
              <div className='text-[8px] uppercase text-muted-foreground'>{kind}</div>
            </div>
          ))}
        </div>

        <div className='mt-2 flex items-center justify-between gap-2'>
          <span className='text-muted-foreground'>Render counters</span>
          <Switch
            size='sm'
            checked={renderCounters}
            onCheckedChange={(v) => setDebugSettings({ renderCounters: v })}
            aria-label='Toggle table render counters'
          />
        </div>

        {Object.keys(perSocket).length > 0 && (
          <table className='mt-2 w-full tabular-nums [&_td]:px-1 [&_th]:px-1 [&_th]:text-left'>
            <thead className='text-[9px] uppercase text-muted-foreground'>
              <tr>
                <th>socket</th>
                <th>sub</th>
                <th>unsub</th>
                <th>emit</th>
                <th>recv</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(perSocket).map(([name, c]) => (
                <tr key={name}>
                  <td className='truncate'>{name}</td>
                  <td className={KIND_COLOR.subscribe}>{c.subscribe}</td>
                  <td className={KIND_COLOR.unsubscribe}>{c.unsubscribe}</td>
                  <td className={KIND_COLOR.emit}>{c.emit}</td>
                  <td className={KIND_COLOR.received}>{c.received}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div className='mt-2 min-h-0 flex-1 space-y-0.5 overflow-y-auto font-mono text-[9px] leading-tight'>
          {log.map((e) => (
            <div key={e.id} className='truncate'>
              <span className={KIND_COLOR[e.kind]}>{e.kind}</span> {e.name}
              {e.detail ? ` (${e.detail})` : ''}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
